import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeeTrackingList, fetchEmployeeLocation } from "../../../core/data/redux/employee/employeeAction";
import { Layout, List, Avatar, Spin, Typography, Badge } from "antd";
import { GoogleMap, LoadScript, Marker, InfoWindowF, Polyline } from "@react-google-maps/api";
import { UserOutlined } from "@ant-design/icons";

// --- (Interfaces) ---
interface Employee {
    EmployeeID: string | number;
    Name: string;
    EmployeeNumber: string;
    CheckedIn: boolean;
    EmployeePictureURL: string | null;
    LastLocation?: {
        Latitude: number | string;
        Longitude: number | string;
        Timestamp: string;
    };
    // Optional; route will actually come from fetchEmployeeLocation
    Route?: { Latitude: number | string; Longitude: number | string }[];
}

type RootState = { employee: { list: Employee[]; loading: boolean } };

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

// Map container
const mapContainerStyle: React.CSSProperties = { width: "100%", height: "100vh" };

// Defaults & India clamp bounds
const defaultCenter = { lat: 21.1458, lng: 79.0882 }; // India-ish
const INDIA_LAT_MIN = 6;
const INDIA_LAT_MAX = 37;
const INDIA_LNG_MIN = 68;
const INDIA_LNG_MAX = 97;

const CLAMP_TO_INDIA = true;         // toggle if needed
const FILTER_ROUTE_TO_TODAY = true;  // toggle if you only want today's track

const AllEmployeesMap: React.FC = () => {
    const dispatch = useDispatch<any>();
    const list = useSelector((s: RootState) => s.employee.list);
    const loading = useSelector((s: RootState) => s.employee.loading);

    const [activeMarker, setActiveMarker] = useState<string | number | null>(null);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

    // Map lifecycle
    const mapRef = useRef<google.maps.Map | null>(null);
    const [mapReady, setMapReady] = useState(false);
    const [tilesLoaded, setTilesLoaded] = useState(false);

    // Route from fetchEmployeeLocation
    const [employeeRoute, setEmployeeRoute] = useState<{ lat: number; lng: number; ts?: string }[]>([]);
    const [isFetchingRoute, setIsFetchingRoute] = useState(false);

    // Poll employees
    useEffect(() => {
        dispatch(fetchEmployeeTrackingList());
        const interval = setInterval(() => dispatch(fetchEmployeeTrackingList()), 30000);
        return () => clearInterval(interval);
    }, [dispatch]);

    // Employees that have a numeric LastLocation
    const employeesWithLocation = useMemo(() => {
        return list.filter((emp) => {
            const lat = parseFloat(emp?.LastLocation?.Latitude as any);
            const lng = parseFloat(emp?.LastLocation?.Longitude as any);
            return emp.LastLocation && !Number.isNaN(lat) && !Number.isNaN(lng);
        });
    }, [list]);

    // Fit to all on first data load (or when no route is selected)
    const fitToAllEmployees = useCallback(() => {
        const map = mapRef.current;
        if (!map || employeesWithLocation.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        employeesWithLocation.forEach((emp) => {
            if (emp.LastLocation) {
                const lat = parseFloat(emp.LastLocation.Latitude as any);
                const lng = parseFloat(emp.LastLocation.Longitude as any);
                if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                    bounds.extend(new google.maps.LatLng(lat, lng));
                }
            }
        });
        if (!bounds.isEmpty()) map.fitBounds(bounds, 64);
    }, [employeesWithLocation]);

    // Fit route bounds
    const fitRouteBounds = useCallback((mapInstance: google.maps.Map, pts: { lat: number; lng: number }[]) => {
        if (!pts?.length) return;
        const bounds = new google.maps.LatLngBounds();
        pts.forEach((pt) => bounds.extend(pt));
        mapInstance.fitBounds(bounds);
        if (pts.length === 1) {
            mapInstance.setCenter(pts[0]);
            mapInstance.setZoom(15);
        }
    }, []);

    // Ensure map size then fit
    const resizeSoon = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                google.maps.event.trigger(map, "resize");
                if (employeeRoute.length) {
                    fitRouteBounds(map, employeeRoute);
                } else {
                    fitToAllEmployees();
                }
            });
        });
    }, [employeeRoute, fitRouteBounds, fitToAllEmployees]);

    // Normalizer for fetchEmployeeLocation rows
    const normalizeLocationRows = (rows: any[]) => {
        let pts = (rows || [])
            .filter((r) => (r?.Latitude ?? r?.latitude) != null && (r?.Longitude ?? r?.longitude) != null)
            .map((r) => ({
                lat: Number(r.Latitude ?? r.latitude),
                lng: Number(r.Longitude ?? r.longitude),
                ts: r.Timestamp ?? r.timestamp ?? r.CreatedAt ?? r.updatedAt,
            }))
            .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));

        // filter to today if desired
        if (FILTER_ROUTE_TO_TODAY && pts.some((p) => p.ts)) {
            const today = new Date();
            const isSameDay = (a: Date, b: Date) =>
                a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
            pts = pts.filter((p) => {
                const d = p.ts ? new Date(p.ts) : null;
                return d ? isSameDay(d, today) : true;
            });
        }

        // sort ascending by time if timestamps exist
        if (pts.length && pts.every((p) => !!p.ts)) {
            pts.sort((a, b) => new Date(a.ts as string).getTime() - new Date(b.ts as string).getTime());
        }

        // clamp to India if desired
        if (CLAMP_TO_INDIA) {
            pts = pts.filter(
                (p) =>
                    p.lat >= INDIA_LAT_MIN &&
                    p.lat <= INDIA_LAT_MAX &&
                    p.lng >= INDIA_LNG_MIN &&
                    p.lng <= INDIA_LNG_MAX
            );
        }

        return pts;
    };

    // When an employee is clicked in the list → fetchEmployeeLocation for full trail
    const handleEmployeeSelectFromList = (employee: Employee) => {
        if (!employee?.LastLocation) return;

        setSelectedEmployee(employee);
        setIsFetchingRoute(true);

        const startLat = parseFloat(employee.LastLocation.Latitude as any);
        const startLng = parseFloat(employee.LastLocation.Longitude as any);

        dispatch(fetchEmployeeLocation(String(employee.EmployeeID)))
            .then((res: any) => {
                const rows = res?.payload ?? res; // depending on your thunk
                let cleaned = normalizeLocationRows(rows);

                // Ensure LastLocation is included as the first point if missing/different
                if (
                    !cleaned.length ||
                    cleaned[0].lat !== startLat ||
                    cleaned[0].lng !== startLng
                ) {
                    cleaned = [{ lat: startLat, lng: startLng, ts: employee.LastLocation?.Timestamp }, ...cleaned];
                }

                setEmployeeRoute(cleaned);

                const map = mapRef.current;
                if (map && cleaned.length) {
                    fitRouteBounds(map, cleaned);
                } else if (map) {
                    map.panTo({ lat: startLat, lng: startLng });
                    map.setZoom(15);
                }
            })
            .catch((err: any) => {
                console.error("Error fetching employee location:", err);
                // Fall back to just the last location
                const lat = Number(employee.LastLocation?.Latitude);
                const lng = Number(employee.LastLocation?.Longitude);
                if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                    const fallback = [{ lat, lng, ts: employee.LastLocation?.Timestamp }];
                    setEmployeeRoute(fallback);
                    const map = mapRef.current;
                    if (map) {
                        fitRouteBounds(map, fallback);
                    }
                } else {
                    setEmployeeRoute([]);
                }
            })
            .finally(() => setIsFetchingRoute(false));
    };

    // Polyline styling
    const polylineOptions: google.maps.PolylineOptions = useMemo(
        () => ({
            strokeColor: "#007bff",
            strokeOpacity: 0.8,
            strokeWeight: 4,
            geodesic: true,
        }),
        []
    );

    return (
        <Layout>
            {/* Sidebar with employee list */}
            <Sider
                width={350}
                theme="light"
                style={{ height: "100vh", overflowY: "auto", borderRight: "1px solid #f0f0f0" }}
            >
                <div style={{ padding: "16px" }}>
                    <Title level={4} style={{ marginBottom: "20px" }}>
                        Employees
                    </Title>
                    {loading && list.length === 0 ? (
                        <div style={{ textAlign: "center", marginTop: "20px" }}>
                            <Spin />
                        </div>
                    ) : (
                        <List
                            dataSource={list}
                            renderItem={(emp) => {
                                const hasLoc = !!emp.LastLocation;
                                const itemNode = (
                                    <List.Item
                                        style={{
                                            cursor: hasLoc ? "pointer" : "default",
                                            padding: "12px 8px",
                                            borderRadius: "4px",
                                        }}
                                        onClick={() => hasLoc && handleEmployeeSelectFromList(emp)}
                                        className="employee-list-item"
                                    >
                                        <List.Item.Meta
                                            avatar={<Avatar src={emp.EmployeePictureURL || undefined} icon={<UserOutlined />} />}
                                            title={
                                                <span>
                                                    {emp.Name}{" "}
                                                    {selectedEmployee?.EmployeeID === emp.EmployeeID && isFetchingRoute && (
                                                        <Badge style={{ marginLeft: 8 }} status="processing" text="Loading route..." />
                                                    )}
                                                </span>
                                            }
                                            description={`#${emp.EmployeeNumber}`}
                                        />
                                    </List.Item>
                                );
                                return itemNode;
                            }}
                        />
                    )}
                </div>
            </Sider>

            {/* Map section */}
            <Content style={{ position: "relative" }}>
                {/* Global overlay spinner while tiles load */}
                {!tilesLoaded && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            background: "rgba(255,255,255,0.65)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Spin size="large" tip={mapReady ? "Loading map tiles..." : "Preparing map..."} />
                    </div>
                )}

                <LoadScript googleMapsApiKey="AIzaSyBJZaHef9KbOAfcdUTHv5osChzfHRc2ne0">
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={defaultCenter}
                        zoom={5}
                        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                        onLoad={(map) => {
                            mapRef.current = map;
                            setMapReady(true);

                            const onceIdle = google.maps.event.addListenerOnce(map, "idle", () => {
                                if (employeeRoute.length) {
                                    fitRouteBounds(map, employeeRoute);
                                } else {
                                    fitToAllEmployees();
                                }
                                setTilesLoaded(true);
                            });

                            resizeSoon();
                        }}
                        onIdle={() => setTilesLoaded(true)}
                    >
                        {/* Markers for all employees */}
                        {employeesWithLocation.map((emp) => {
                            const lat = parseFloat(emp.LastLocation!.Latitude as any);
                            const lng = parseFloat(emp.LastLocation!.Longitude as any);
                            return (
                                <Marker
                                    key={String(emp.EmployeeID)}
                                    position={{ lat, lng }}
                                    onMouseOver={() => setActiveMarker(emp.EmployeeID)}
                                    onMouseOut={() => setActiveMarker(null)}
                                >
                                    {activeMarker === emp.EmployeeID && (
                                        <InfoWindowF onCloseClick={() => setActiveMarker(null)} position={{ lat, lng }}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    background: "white",
                                                    padding: "5px",
                                                    borderRadius: "4px",
                                                }}
                                            >
                                                <Avatar size="large" src={emp.EmployeePictureURL || undefined} icon={<UserOutlined />} />
                                                <div>
                                                    <Text strong>{emp.Name}</Text>
                                                    <br />
                                                    <Text type="secondary">
                                                        Last seen:{" "}
                                                        {new Date(emp.LastLocation!.Timestamp).toLocaleTimeString([], {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </Text>
                                                </div>
                                            </div>
                                        </InfoWindowF>
                                    )}
                                </Marker>
                            );
                        })}

                        {/* Selected employee route + labeled start/end markers */}
                        {employeeRoute.length > 0 && (
                            <>
                                <Marker
                                    position={employeeRoute[0]}
                                    label={{ text: "Start", color: "black", fontSize: "12px", fontWeight: "bold" }}
                                    icon={{ url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png" }}
                                />
                                <Marker
                                    position={employeeRoute[employeeRoute.length - 1]}
                                    label={{ text: "End", color: "black", fontSize: "12px", fontWeight: "bold" }}
                                    icon={{ url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png" }}
                                />
                                {employeeRoute.length >= 2 && (
                                    <Polyline path={employeeRoute.map(({ lat, lng }) => ({ lat, lng }))} options={polylineOptions} />
                                )}
                            </>
                        )}
                    </GoogleMap>
                </LoadScript>
            </Content>

            <style>{`.employee-list-item:hover { background-color: #f0f2f5; }`}</style>
        </Layout>
    );
};

export default AllEmployeesMap;
